(ns hai.notes.create
  "Standalone note creation in the expanded editor ([[hai.notes.editor]]): the
   universal bar, the notes wall, and the mobile add sheet open it on a fresh
   regular note that auto-saves as you type — the same behaviour as the home
   scratchpad, but the note never touches the pad. Mount [[overlay]] once at
   the app root."
  (:require
   [clojure.string :as str]
   [hai.notes.data :as notes-data]
   [hai.notes.editor :as editor]
   [hai.notes.stacks :as stacks]
   [hai.ui.rf-sugar :refer [<s >e defevent defevent-fx defsub]]
   [hai.web-app.viewport :as viewport]
   [reagent.core :as r]))

(defsub ::open? [db]
  (get db ::open? false))

(defsub ::note [db]
  (get db ::note))

(defsub ::tags [db]
  (get-in db [::note :note/tags] []))

(defsub ::link-urls [db]
  (->> (get-in db [::note :note/activities])
       (keep :activity/url)
       vec))

(defsub ::contacts [db]
  (get-in db [::note :note/contacts] []))

(defsub ::linked-notes [db]
  (get-in db [::note :note/notes] []))

(defsub ::linked-tasks [db]
  (get-in db [::note :note/tasks] []))

(defsub ::saving? [db]
  (get db ::saving? false))

(defsub ::generation [db]
  (get db ::generation 0))

(defn form-id
  "One form state per creation session — bumping the generation on open gives
   every session a blank form."
  [generation]
  [::form generation])

(defevent ::open [db _]
  (-> db
      (dissoc ::note ::save-request-id)
      (assoc ::open? true ::saving? false)
      (update ::generation (fnil inc 0))))

;; Refresh whichever note list the user is looking at, so the created note
;; shows without a manual reload — and nothing otherwise.
(defn- visible-list-refresh-fx [db]
  (case (get-in db [:routing/current-route :data :name])
    :timelines/view [[:dispatch [:timeline/load-entries]]]
    :notes/view     [[:dispatch [:table/resolve notes-data/table-id]]
                     [:dispatch [::notes-data/load-facets]]]
    []))

(defevent-fx ::close
  [{:keys [db]} _]
  (cond-> {:db (assoc db ::open? false)}
    (get-in db [::note :note/uuid]) (assoc :fx (visible-list-refresh-fx db))))

(defevent-fx ::save
  [{:keys [db]} {:keys [text link-urls tag-uuids tag-names untag-uuids contact-uuids
                        link-note-uuids task-uuids]
                 :as payload}]
  (let [note-uuid (get-in db [::note :note/uuid])]
    (cond
      ;; Nothing to create from yet — no text, links, tags, contacts, notes or
      ;; tasks to hold.
      (and (nil? note-uuid) (str/blank? text) (empty? link-urls)
           (empty? tag-uuids) (empty? tag-names) (empty? contact-uuids)
           (empty? link-note-uuids) (empty? task-uuids))
      {:db db}

      :else
      (let [req-id (random-uuid)
            params (cond-> {:note/source :web-app}
                     ;; A payload without :text leaves the note's text alone;
                     ;; creation always writes it (note/upsert requires text).
                     (or (contains? payload :text) (nil? note-uuid))
                     (assoc :note/text (or text ""))
                     note-uuid (assoc :note/uuid note-uuid)
                     (seq link-urls) (assoc :activity/urls link-urls)
                     (seq tag-uuids) (assoc :note/tag-uuids tag-uuids)
                     (seq tag-names) (assoc :note/tag-names tag-names)
                     (seq untag-uuids) (assoc :note/untag-uuids untag-uuids)
                     (seq contact-uuids) (assoc :note/contact-uuids contact-uuids)
                     (seq link-note-uuids) (assoc :note/note-uuids link-note-uuids)
                     (seq task-uuids) (assoc :note/task-uuids task-uuids))]
        {:db (assoc db ::saving? true ::save-request-id req-id)
         :resolve {:request-id req-id
                   :query [{(list 'note/upsert params) editor/note-eql}]
                   :transform-fn (fn [resp] (get resp 'note/upsert))
                   :store-in [::note]
                   :on-success-fx [[:dispatch [::save-success]]]
                   :on-error-fx [[:dispatch [::save-error]]]}}))))

(defevent-fx ::save-success
  [{:keys [db]} _]
  ;; A pending autosave can land after the editor closed (the unmount flush);
  ;; refresh the visible list then, since ::close couldn't have.
  (cond-> {:db (-> db (assoc ::saving? false) (dissoc ::save-request-id))}
    (not (::open? db)) (assoc :fx (visible-list-refresh-fx db))))

(defevent ::save-error [db _]
  (-> db
      (assoc ::saving? false)
      (dissoc ::save-request-id)))

;; Optimistic: the link disappears immediately; the response reconciles with
;; server truth via store-in, and an error restores the snapshot.
(defevent-fx ::remove-link
  [{:keys [db]} link-url]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    (let [previous (get-in db [::note :note/activities])]
      {:db (update-in db [::note :note/activities]
                      (fn [activities]
                        (vec (remove #(= link-url (:activity/url %)) activities))))
       :resolve {:query [{(list 'note/remove-link {:note/uuid note-uuid
                                                   :activity/url link-url})
                          [:note/uuid {:note/activities [:activity/uuid :activity/url]}]}]
                 :transform-fn (fn [resp] (get-in resp ['note/remove-link :note/activities]))
                 :store-in [::note :note/activities]
                 :on-error-fx [[:dispatch [::restore-links previous]]
                               [:dispatch [:toast/error {:text "Could not remove link. Try again."}]]]}})))

(defevent ::restore-links [db activities]
  (assoc-in db [::note :note/activities] activities))

;; Optimistic: mirrors ::remove-link for contact links.
(defevent-fx ::remove-contact
  [{:keys [db]} contact-uuid]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    (let [previous (get-in db [::note :note/contacts])]
      {:db (update-in db [::note :note/contacts]
                      (fn [contacts]
                        (vec (remove #(= contact-uuid (:contact/uuid %)) contacts))))
       :resolve {:query [{(list 'note/remove-contact {:note/uuid note-uuid
                                                      :contact/uuid contact-uuid})
                          [:note/uuid {:note/contacts [:contact/uuid :contact/full-name :contact/organisation?]}]}]
                 :transform-fn (fn [resp] (get-in resp ['note/remove-contact :note/contacts]))
                 :store-in [::note :note/contacts]
                 :on-error-fx [[:dispatch [::restore-contacts previous]]
                               [:dispatch [:toast/error {:text "Could not remove contact. Try again."}]]]}})))

(defevent ::restore-contacts [db contacts]
  (assoc-in db [::note :note/contacts] contacts))

;; Optimistic, mirroring ::remove-contact — for linked notes and tasks.
(defevent-fx ::remove-note-link
  [{:keys [db]} linked-uuid]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    (let [previous (get-in db [::note :note/notes])]
      {:db (update-in db [::note :note/notes]
                      (fn [notes]
                        (vec (remove #(= linked-uuid (:note/uuid %)) notes))))
       :resolve {:query [{(list 'note/remove-note {:note/uuid note-uuid
                                                   :note/linked-uuid linked-uuid})
                          [:note/uuid {:note/notes [:note/uuid :note/text]}]}]
                 :transform-fn (fn [resp] (get-in resp ['note/remove-note :note/notes]))
                 :store-in [::note :note/notes]
                 :on-error-fx [[:dispatch [::restore-note-links previous]]
                               [:dispatch [:toast/error {:text "Could not remove linked note. Try again."}]]]}})))

(defevent ::restore-note-links [db notes]
  (assoc-in db [::note :note/notes] notes))

(defevent-fx ::remove-task
  [{:keys [db]} task-uuid]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    (let [previous (get-in db [::note :note/tasks])]
      {:db (update-in db [::note :note/tasks]
                      (fn [tasks]
                        (vec (remove #(= task-uuid (:task/uuid %)) tasks))))
       :resolve {:query [{(list 'note/remove-task {:note/uuid note-uuid
                                                   :task/uuid task-uuid})
                          [:note/uuid {:note/tasks [:task/uuid :task/title]}]}]
                 :transform-fn (fn [resp] (get-in resp ['note/remove-task :note/tasks]))
                 :store-in [::note :note/tasks]
                 :on-error-fx [[:dispatch [::restore-tasks previous]]
                               [:dispatch [:toast/error {:text "Could not remove linked task. Try again."}]]]}})))

(defevent ::restore-tasks [db tasks]
  (assoc-in db [::note :note/tasks] tasks))

;; Re-resolve the note in place — after the share dialog closes, so
;; freshly-added collaborators surface in the footer.
(defevent-fx ::refresh
  [{:keys [db]} _]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    (let [ident [:note/uuid note-uuid]]
      {:resolve {:query [{ident editor/note-eql}]
                 :transform-fn (fn [resp] (get resp ident))
                 :store-in [::note]}})))

(defevent-fx ::delete
  [{:keys [db]} _]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    ;; Cancel any in-flight autosave so its response can't re-store the note
    ;; after it's been deleted.
    (let [pending-save-id (::save-request-id db)]
      {:db (cond-> (-> db (assoc ::saving? true) (dissoc ::save-request-id))
             pending-save-id (update :resolve/canceled-requests (fnil conj #{}) pending-save-id))
       :resolve {:query [{(list 'note/delete {:note/uuid note-uuid}) [:note/uuid]}]
                 :on-success-fx [[:dispatch [::delete-success]]]
                 :on-error-fx [[:dispatch [::save-error]]
                               [:dispatch [:toast/error {:text "Could not delete note. Try again."}]]]}})))

(defevent ::delete-success [db _]
  (-> db
      (dissoc ::note)
      (assoc ::open? false ::saving? false)))

(def extras-handlers
  "The creation note's tag/link save wiring for [[hai.notes.form/note-extras]]."
  {:on-add-tag     (fn [tag] (>e [::save (editor/tag-save-payload tag)]))
   :on-remove-tag  (fn [tag] (>e [::save {:untag-uuids [(:group/uuid tag)]}]))
   :on-add-link    (fn [link-url] (>e [::save {:link-urls [link-url]}]))
   :on-remove-link (fn [link-url] (>e [::remove-link link-url]))
   :on-add-contact    (fn [contact-uuid] (>e [::save {:contact-uuids [contact-uuid]}]))
   :on-remove-contact (fn [contact-uuid] (>e [::remove-contact contact-uuid]))
   :on-add-note       (fn [linked-uuid] (>e [::save {:link-note-uuids [linked-uuid]}]))
   :on-remove-note    (fn [linked-uuid] (>e [::remove-note-link linked-uuid]))
   :on-add-task       (fn [task-uuid] (>e [::save {:task-uuids [task-uuid]}]))
   :on-remove-task    (fn [task-uuid] (>e [::remove-task task-uuid]))
   :on-open-entity    (fn [entity] (>e [::notes-data/open-entity entity]))})

(defn overlay
  "The \"Creating note\" editor — the expanded modal on desktop, the
   full-screen editor on mobile. Mount once at the app root; opened via
   [[::open]]."
  []
  (r/with-let [!autosave (atom nil)]
    (let [open?        @(<s [::open?])
          mobile?      @(<s [::viewport/mobile?])
          note         @(<s [::note])
          tags         @(<s [::tags])
          link-urls    @(<s [::link-urls])
          contacts     @(<s [::contacts])
          linked-notes @(<s [::linked-notes])
          linked-tasks @(<s [::linked-tasks])
          saving?      @(<s [::saving?])
          generation   @(<s [::generation])
          editor-props {:title           "Creating note"
                        :note            note
                        :tags            tags
                        :link-urls       link-urls
                        :contacts        contacts
                        :linked-notes    linked-notes
                        :linked-tasks    linked-tasks
                        :saving?         saving?
                        :form-id         (form-id generation)
                        :!autosave       !autosave
                        :on-save-text    (fn [text] (>e [::save {:text text}]))
                        :extras-handlers extras-handlers
                        :on-share-closed #(>e [::refresh])
                        :on-close        #(>e [::close])
                        :on-delete       (fn []
                                           ;; Drop the pending autosave so the
                                           ;; unmount flush can't re-create the
                                           ;; note being deleted.
                                           (when-let [{:keys [discard!]} @!autosave]
                                             (discard!))
                                           (>e [::delete]))}]
      (if mobile?
        (when open?
          [editor/screen editor-props])
        [editor/dialog
         {:open?    open?
          :on-close #(>e [::close])
          :title    "Creating note"}
         (when open?
           [editor/modal-content editor-props])]))))
